<template>
  <!--
    DocumentEditModal.vue - Modal til redigering af dokument
    ========================================================
    Side-by-side layout med preview til venstre og formular til højre.

    Props:
    - document: Dokument objekt der redigeres
    - categories: Tilgængelige kategorier til dropdown
  -->
  <b-modal
    id="document-edit-modal"
    :visible="true"
    size="xl"
    :title="$t('DEBTIA_DOC_EDIT_TITLE')"
    hide-footer
    modal-class="document-edit-fullheight"
    @hidden="$emit('close')"
  >
    <template #modal-header>
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="modal-title">
          <feather-icon
            icon="EditIcon"
            class="mr-50"
          />
          {{ localDoc.title || (localDoc.file && localDoc.file.name) || $t('DEBTIA_DOC_EDIT_TITLE') }}
        </h5>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon"
          @click="$emit('close')"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </div>
    </template>

    <b-row>
      <!-- Left: Preview -->
      <b-col
        cols="12"
        lg="6"
        class="preview-col"
      >
        <div class="preview-container">
          <div class="preview-header">
            <span class="text-muted">
              {{ getFileExtension(localDoc.file).toUpperCase() }}
              &middot;
              {{ formatFileSize(localDoc.file && localDoc.file.size) }}
            </span>
          </div>

          <div class="preview-content">
            <!-- PDF Preview -->
            <iframe
              v-if="isPdf(localDoc.file)"
              :src="previewUrl"
              type="application/pdf"
              class="pdf-preview"
            />

            <!-- Image Preview -->
            <img
              v-else-if="isImage(localDoc.file)"
              :src="previewUrl"
              :alt="localDoc.file && localDoc.file.name"
              class="image-preview"
            >

            <!-- Word documents -->
            <div
              v-else-if="isWordDocument(localDoc.file)"
              class="preview-placeholder"
            >
              <feather-icon
                icon="FileTextIcon"
                size="48"
                class="text-primary mb-1"
              />
              <p class="font-weight-bold">
                {{ localDoc.file && localDoc.file.name }}
              </p>
              <small class="text-muted">
                {{ $t('DEBTIA_DOC_EDIT_WORD_NO_PREVIEW') }}
              </small>
            </div>

            <!-- Other files -->
            <div
              v-else
              class="preview-placeholder"
            >
              <feather-icon
                icon="FileIcon"
                size="48"
                class="text-muted mb-1"
              />
              <p class="text-muted">
                {{ $t('DEBTIA_DOC_EDIT_NO_PREVIEW') }}
              </p>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Right: Form -->
      <b-col
        cols="12"
        lg="6"
        class="form-col"
      >
        <div class="document-form">
          <!-- Category Selector -->
          <b-form-group
            :label="$t('DEBTIA_CATEGORY')"
            label-for="category"
          >
            <v-select
              id="category"
              v-model="localDoc.category"
              :options="categories"
              :reduce="c => c.value"
              label="label"
              :clearable="false"
            />
          </b-form-group>

          <!-- Common Fields -->
          <b-form-group
            :label="$t('DEBTIA_DOC_EDIT_LABEL_TITLE')"
            label-for="title"
          >
            <b-form-input
              id="title"
              v-model="localDoc.title"
              :placeholder="$t('DEBTIA_PLACEHOLDER_INVOICE_TITLE')"
            />
          </b-form-group>

          <!-- Grundlag Fields -->
          <template v-if="localDoc.category === 'grundlag'">
            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_INVOICE_DATE')"
                  label-for="sent-at"
                >
                  <b-form-datepicker
                    id="sent-at"
                    v-model="localDoc.sentAt"
                    locale="da"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                    :placeholder="$t('DEBTIA_FORM_SELECT_DATE')"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_DUE_DATE')"
                  label-for="due-date"
                >
                  <b-form-datepicker
                    id="due-date"
                    v-model="localDoc.dueDate"
                    locale="da"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                    :placeholder="$t('DEBTIA_FORM_SELECT_DATE')"
                    :min="localDoc.sentAt"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_AMOUNT_DKK')"
                  label-for="amount"
                >
                  <b-input-group :append="$t('DEBTIA_COMMON_CURRENCY_KR')">
                    <b-form-input
                      id="amount"
                      v-model="localDoc.paymentAmount"
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="0,00"
                    />
                  </b-input-group>
                  <small class="text-muted mt-25 d-block">{{ $t('DEBTIA_AMOUNT_HINT') }}</small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_ALREADY_PAID')"
                  label-for="paid"
                >
                  <b-input-group :append="$t('DEBTIA_COMMON_CURRENCY_KR')">
                    <b-form-input
                      id="paid"
                      v-model="localDoc.amountPaid"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0,00"
                    />
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_INTEREST_RATE')"
                  label-for="interest"
                >
                  <b-input-group append="%">
                    <b-form-input
                      id="interest"
                      v-model="localDoc.interestRate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0,0"
                    />
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <!-- Kreditnota Fields -->
          <template v-else-if="localDoc.category === 'kreditnoter'">
            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_DATE')"
                  label-for="credit-date"
                >
                  <b-form-datepicker
                    id="credit-date"
                    v-model="localDoc.sentAt"
                    locale="da"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_AMOUNT_DKK')"
                  label-for="credit-amount"
                >
                  <b-input-group :append="$t('DEBTIA_COMMON_CURRENCY_KR')">
                    <b-form-input
                      id="credit-amount"
                      v-model="localDoc.creditNoteAmount"
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="0,00"
                    />
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <!-- Rykker Fields -->
          <template v-else-if="localDoc.category === 'rykkere'">
            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_DOC_DATE')"
                  label-for="doc-date"
                >
                  <b-form-datepicker
                    id="doc-date"
                    v-model="localDoc.documentDate"
                    locale="da"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_DUE_DATE')"
                  label-for="reminder-due"
                >
                  <b-form-datepicker
                    id="reminder-due"
                    v-model="localDoc.dueDate"
                    locale="da"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_LABEL_REMINDER_FEE')"
                  label-for="reminder-fee"
                >
                  <b-input-group :append="$t('DEBTIA_COMMON_CURRENCY_KR')">
                    <b-form-input
                      id="reminder-fee"
                      v-model="localDoc.reminderFee"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      placeholder="0"
                      @input="validateReminderFee"
                    />
                  </b-input-group>
                  <small class="text-muted">{{ $t('DEBTIA_DOC_EDIT_MAX_FEE_HINT') }}</small>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  :label="$t('DEBTIA_DOC_EDIT_LABEL_INTEREST_AMOUNT')"
                  label-for="interest-amount"
                >
                  <b-input-group :append="$t('DEBTIA_COMMON_CURRENCY_KR')">
                    <b-form-input
                      id="interest-amount"
                      v-model="localDoc.interestAmount"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0,00"
                    />
                  </b-input-group>
                  <small class="text-muted">{{ $t('DEBTIA_DOC_EDIT_INTEREST_HINT') }}</small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Kompensationsgebyr Toggle -->
            <b-form-group class="mt-1">
              <b-form-checkbox
                v-model="localDoc.hasCompensationFee"
                :disabled="compensationFeeDisabled"
                switch
                size="lg"
              >
                <span class="font-weight-bold">{{ $t('DEBTIA_DOC_EDIT_COMPENSATION_FEE') }}</span>
              </b-form-checkbox>
              <small
                v-if="compensationFeeDisabled && !localDoc.hasCompensationFee"
                class="text-warning d-block mt-50"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('DEBTIA_DOC_EDIT_COMPENSATION_ALREADY_SET') }}
              </small>
              <small
                v-else
                class="text-muted d-block mt-50"
              >
                {{ $t('DEBTIA_DOC_EDIT_COMPENSATION_ONCE') }}
              </small>
            </b-form-group>

            <!-- AI Warnings -->
            <div
              v-if="localDoc.aiWarnings && localDoc.aiWarnings.length > 0"
              class="ai-warnings mt-2"
            >
              <div
                v-for="(warning, idx) in localDoc.aiWarnings"
                :key="idx"
                class="warning-item"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="14"
                  class="text-warning mr-50"
                />
                <small class="text-warning">{{ warning }}</small>
              </div>
            </div>
          </template>

          <!-- Øvrig dokumentation Fields -->
          <template v-else-if="localDoc.category === 'oevrig'">
            <b-form-group
              :label="$t('DEBTIA_DOC_EDIT_LABEL_DOC_TYPE')"
              label-for="file-category"
            >
              <v-select
                id="file-category"
                v-model="localDoc.categoryId"
                :options="fileCategories"
                :reduce="c => c.value"
                label="label"
                :placeholder="$t('DEBTIA_DOC_EDIT_SELECT_TYPE')"
                :loading="loadingCategories"
              />
            </b-form-group>
          </template>

          <!-- AI Info Box -->
          <div
            v-if="localDoc.aiAnalyzed"
            class="ai-info-box mt-2"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="CpuIcon"
                size="16"
                class="text-primary mr-50"
              />
              <span class="font-weight-bold">{{ $t('DEBTIA_DOC_EDIT_AI_ANALYSIS') }}</span>
              <!-- Vises kun når modellen faktisk har afgivet en vurdering.
                   Uden tal er det bedre at vise ingenting end at opfinde et. -->
              <b-badge
                v-if="localDoc.aiDataConfidence !== null && localDoc.aiDataConfidence !== undefined"
                :variant="getConfidenceVariant(localDoc.aiDataConfidence)"
                class="ml-auto"
              >
                {{ localDoc.aiDataConfidence }}% {{ $t('DEBTIA_DOC_EDIT_AI_CONFIDENCE') }}
              </b-badge>
            </div>
            <small class="text-muted">
              {{ $t('DEBTIA_DOC_EDIT_AI_PREFILLED') }}
            </small>

            <!-- Tvivl om hvem der er skyldner er den dyreste fejl i systemet,
                 så den får sin egen advarsel frem for at gemme sig i et tal. -->
            <div
              v-if="lowDebtorConfidence"
              class="ai-warning mt-50"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="14"
                class="mr-25"
              />
              <small>{{ $t('DEBTIA_DOC_EDIT_AI_CHECK_DEBTOR') }}</small>
            </div>

            <div
              v-for="(warning, index) in (localDoc.aiWarnings || [])"
              :key="index"
              class="ai-warning mt-50"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="14"
                class="mr-25"
              />
              <small>{{ warning }}</small>
            </div>

            <!-- Lange PDF'er: sig det, hvis ikke alt er blevet læst. -->
            <div
              v-if="localDoc.aiPages && localDoc.aiPages.analyzed < localDoc.aiPages.total"
              class="ai-warning mt-50"
            >
              <feather-icon
                icon="FileTextIcon"
                size="14"
                class="mr-25"
              />
              <small>
                {{ $t('DEBTIA_DOC_EDIT_AI_PARTIAL_PDF', { analyzed: localDoc.aiPages.analyzed, total: localDoc.aiPages.total }) }}
              </small>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="form-actions mt-2 pt-2 border-top">
            <b-button
              variant="outline-secondary"
              @click="$emit('close')"
            >
              {{ $t('DEBTIA_DOC_EDIT_CANCEL') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="!isFormValid"
              @click="handleSave"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              {{ $t('DEBTIA_DOC_EDIT_SAVE') }}
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BFormDatepicker,
  BInputGroup,
  BBadge,
  BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref, computed, onMounted, onBeforeUnmount,
} from '@vue/composition-api'
import axios from '@axios'
import constants from '@/app-configs/constants'

export default {
  name: 'DocumentEditModal',

  components: {
    BModal,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormDatepicker,
    BInputGroup,
    BBadge,
    BFormCheckbox,
    vSelect,
  },

  props: {
    document: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    hasCompensationFeeSet: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    // Local copy of document for editing
    const localDoc = ref({ ...props.document })

    // File categories for "øvrig" type
    const fileCategories = ref([])
    const loadingCategories = ref(false)

    // Preview URL
    const previewUrl = ref('')

    // Fetch file categories on mount
    onMounted(async () => {
      // Create preview URL
      if (props.document.file) {
        previewUrl.value = URL.createObjectURL(props.document.file)
      }

      // Fetch file categories
      try {
        loadingCategories.value = true
        const response = await axios.get(`${constants.baseUrl}/settings/file-categories-dropdown`)
        if (response.data.data) {
          fileCategories.value = response.data.data
        }
      } catch (error) {
        console.error('Could not fetch file categories:', error)
      } finally {
        loadingCategories.value = false
      }
    })

    // Cleanup preview URL
    onBeforeUnmount(() => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value)
      }
    })

    // File helpers
    function getFileExtension(file) {
      if (!file) return ''
      return file.name.split('.').pop().toLowerCase()
    }

    function formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
    }

    function isPdf(file) {
      if (!file) return false
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    }

    function isImage(file) {
      if (!file) return false
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png']
      return imageTypes.includes(file.type) || /\.(jpg|jpeg|png)$/i.test(file.name)
    }

    function isWordDocument(file) {
      if (!file) return false
      const ext = getFileExtension(file)
      return ['doc', 'docx'].includes(ext)
    }

    function getConfidenceVariant(confidence) {
      if (confidence >= 90) return 'light-success'
      if (confidence >= 70) return 'light-warning'
      return 'light-danger'
    }

    // Under 80 er der reel tvivl om, hvem der skylder pengene. Det skal en
    // sagsbehandler se, ikke gætte sig til ud fra et procenttal.
    const lowDebtorConfidence = computed(() => {
      const value = localDoc.value.aiDebtorConfidence
      return typeof value === 'number' && value < 80
    })

    // Kompensationsgebyr disabled check
    // Disabled hvis en anden rykker allerede har kompensationsgebyr (og dette dokument ikke har det)
    const compensationFeeDisabled = computed(() => props.hasCompensationFeeSet && !localDoc.value.hasCompensationFee)

    // Validate reminder fee (cap at 100 kr)
    function validateReminderFee() {
      const fee = Number(localDoc.value.reminderFee)
      if (fee > 100) {
        localDoc.value.reminderFee = 100
      }
    }

    // Validation
    const isFormValid = computed(() => {
      const doc = localDoc.value

      // Check required fields based on category
      switch (doc.category) {
        case 'grundlag':
          return doc.sentAt && doc.dueDate && doc.paymentAmount && parseFloat(doc.paymentAmount) >= 1

        case 'kreditnoter':
          return doc.sentAt && doc.creditNoteAmount && parseFloat(doc.creditNoteAmount) >= 1

        case 'rykkere':
          return true // All fields optional

        case 'oevrig':
          return doc.title && doc.categoryId

        default:
          return true
      }
    })

    function handleSave() {
      // Update isValid based on current form state
      localDoc.value.isValid = isFormValid.value
      emit('save', localDoc.value)
    }

    return {
      localDoc,
      fileCategories,
      loadingCategories,
      previewUrl,
      isFormValid,
      compensationFeeDisabled,
      getFileExtension,
      formatFileSize,
      isPdf,
      isImage,
      isWordDocument,
      getConfidenceVariant,
      lowDebtorConfidence,
      validateReminderFee,
      handleSave,
    }
  },
}
</script>

<style lang="scss" scoped>
.preview-col {
  border-right: 1px solid #e9ecef;
  padding-right: 1.5rem;

  @media (max-width: 991px) {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    padding-right: 0;
    padding-bottom: 1.5rem;
    margin-bottom: 1.5rem;
  }
}

.form-col {
  padding-left: 1.5rem;

  @media (max-width: 991px) {
    padding-left: 0;
  }
}

.preview-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-header {
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.375rem 0.375rem 0 0;
  border: 1px solid #e9ecef;
  border-bottom: none;
}

.preview-content {
  flex: 1;
  border: 1px solid #e9ecef;
  border-radius: 0 0 0.375rem 0.375rem;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  overflow: hidden;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  min-height: 300px;
  border: none;
}

.image-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-placeholder {
  text-align: center;
  padding: 2rem;
}

.document-form {
  overflow-y: auto;
  padding-right: 0.5rem;
}

.ai-info-box {
  padding: 0.75rem;
  background: rgba(115, 103, 240, 0.05);
  border: 1px solid rgba(115, 103, 240, 0.2);
  border-radius: 0.375rem;
}

// Forbehold fra modellen. Skal kunne ses uden at ligne en fejl — det er en
// opfordring til at kigge efter, ikke en blokering.
.ai-warning {
  display: flex;
  align-items: flex-start;
  color: #b06000;

  svg {
    flex-shrink: 0;
    margin-top: 0.15em;
  }
}

.ai-warnings {
  padding: 0.75rem;
  background: rgba(255, 159, 67, 0.1);
  border: 1px solid rgba(255, 159, 67, 0.3);
  border-radius: 0.375rem;

  .warning-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

// ---- Dark Mode ----
.dark-layout {
  .preview-col {
    border-color: #163350;
  }

  .preview-header {
    background: #0b1e32;
    border-color: #163350;
  }

  .preview-content {
    background: #091828;
    border-color: #163350;
  }

  .ai-info-box {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.2);
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Fullheight modal — same padding top/bottom as sides
.document-edit-fullheight {
  .modal-dialog {
    max-width: 1140px;
    margin: 1.75rem auto;
    height: calc(100vh - 3.5rem);
    display: flex;
    align-items: stretch;
  }

  .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .modal-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    > .row {
      flex: 1;
      overflow: hidden;

      > [class*="col-lg-6"]:first-child {
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .preview-container {
          flex: 1;
          overflow: hidden;
        }

        .preview-content {
          flex: 1;
          min-height: 0;

          iframe {
            height: 100%;
          }
        }
      }

      > [class*="col-lg-6"]:last-child {
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .document-form {
          flex: 1;
          overflow-y: auto;
        }
      }
    }
  }
}
</style>
